import { jwtDecode } from 'jwt-decode';
import { type QueryClient } from '@tanstack/react-query';
import { ApiError, type User, V2AdminUsersService } from 'src/api/generated';
import { adminUsersQueryKeys } from 'src/modules/shared/hooks/api/admin/users';
import { sleep } from 'src/utils/clock';
import {
  AUTH_RETRY_ATTEMPTS,
  BASE_RETRY_DELAY_IN_MS,
  CURRENT_USER_RETRY_ATTEMPTS,
  CURRENT_USER_TIMEOUT_IN_MS,
  IDENTITY_LINKED_REAUTHENTICATION_REQUIRED,
  IDENTITY_LINKING_MANUAL_RECOVERY_REQUIRED,
  PERMISSIONS_REFRESH_ATTEMPTS,
  TOKEN_REQUEST_TIMEOUT_IN_MS,
} from '../../constants/auth/auth-bootstrap.constants';
import { AuthBootstrapError } from '../../errors/auth/auth-bootstrap.error';
import type {
  AuthBootstrapPhase,
  GetAccessTokenSilently,
  JwtPayloadWithPermissions,
} from '../../types/auth/auth-bootstrap.type';
import type { AuthBootstrapRecoveryReason } from '../../types/auth/auth-recovery.type';
import { isRecord } from '../types.util';
import {
  getAuthErrorCode,
  getAuthErrorKind,
  getAuthErrorMessage,
} from './auth-error.util';
import { hasAuthBootstrapRecoveryAttempted } from './auth-recovery.util';

/**
 * Decodes permissions from an Auth0 access token.
 *
 * @param token - JWT access token issued by Auth0.
 * @returns Permission names included in the token.
 */
export const decodePermissions = (token: string): string[] => {
  const decoded = jwtDecode<JwtPayloadWithPermissions>(token);
  return decoded.permissions ?? [];
};

/**
 * Normalizes unknown errors into Error instances.
 *
 * @param error - Unknown error captured during bootstrap.
 * @param fallbackMessage - Message used when the error has no useful text.
 * @returns Normalized Error instance.
 */
export const toErrorLike = (error: unknown, fallbackMessage: string): Error => {
  if (error instanceof Error) {
    return error;
  }

  const message = getAuthErrorMessage(error) ?? fallbackMessage;
  const nextError = new Error(message);

  if (isRecord(error)) {
    Object.assign(nextError, error);
  }

  return nextError;
};

/**
 * Computes the delay for a retry attempt using exponential backoff.
 *
 * @param attempt - Current retry attempt number.
 * @returns Delay in milliseconds.
 */
export const getRetryDelay = (attempt: number): number => {
  return BASE_RETRY_DELAY_IN_MS * Math.pow(2, Math.max(0, attempt - 1));
};

/**
 * Runs an async operation with a timeout guard.
 *
 * @param operation - Operation to execute.
 * @param timeoutInMs - Max time to wait before failing.
 * @param timeoutMessage - Internal error message for the timeout.
 * @param phase - Bootstrap phase associated with the timeout.
 * @returns Operation result.
 */
export const withTimeout = async <T>(
  operation: () => Promise<T>,
  timeoutInMs: number,
  timeoutMessage: string,
  phase: AuthBootstrapPhase
): Promise<T> => {
  return await new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(
        new AuthBootstrapError(timeoutMessage, {
          canRetry: true,
          kind: 'retryable',
          messageKey: 'shared.auth.bootstrap.error.timeout',
          phase,
        })
      );
    }, timeoutInMs);

    void operation()
      .then((result) => {
        window.clearTimeout(timeoutId);
        resolve(result);
      })
      .catch((error) => {
        window.clearTimeout(timeoutId);
        reject(toErrorLike(error, 'Auth bootstrap operation failed.'));
      });
  });
};

/**
 * Converts arbitrary bootstrap failures into typed auth bootstrap errors.
 *
 * @param error - Failure captured during bootstrap.
 * @param phase - Bootstrap phase that raised the failure.
 * @returns Typed auth bootstrap error.
 */
export const normalizeBootstrapError = (
  error: unknown,
  phase: AuthBootstrapPhase = 'unknown'
): AuthBootstrapError => {
  if (error instanceof AuthBootstrapError) {
    return error;
  }

  if (error instanceof ApiError) {
    if (getAuthErrorCode(error) === IDENTITY_LINKED_REAUTHENTICATION_REQUIRED) {
      return new AuthBootstrapError(
        'The alternate login identity was linked and needs a new token.',
        {
          cause: error,
          kind: 'identity-linked',
          messageKey: 'shared.auth.bootstrap.error.sessionInvalid',
          phase,
        }
      );
    }

    if (getAuthErrorCode(error) === IDENTITY_LINKING_MANUAL_RECOVERY_REQUIRED) {
      return new AuthBootstrapError(
        'The alternate login identity requires manual recovery.',
        {
          cause: error,
          kind: 'manual-recovery',
          messageKey: 'shared.auth.bootstrap.error.manualRecovery',
          phase,
        }
      );
    }

    if (error.status >= 500) {
      return new AuthBootstrapError(
        'Current user bootstrap failed due to a temporary backend error.',
        {
          canRetry: true,
          cause: error,
          kind: 'retryable',
          messageKey: 'shared.auth.bootstrap.error.backendUnavailable',
          phase,
        }
      );
    }

    if (error.status === 401) {
      return new AuthBootstrapError(
        'Authentication expired during bootstrap.',
        {
          cause: error,
          kind: 'reauth-required',
          messageKey: 'shared.auth.bootstrap.error.sessionInvalid',
          phase,
        }
      );
    }

    if (error.status === 403 || error.status === 404) {
      return new AuthBootstrapError(
        'The current account could not be linked to a local user.',
        {
          cause: error,
          kind: 'access-denied',
          messageKey: 'shared.auth.bootstrap.error.localUserLinkFailed',
          phase,
        }
      );
    }
  }

  const authErrorKind = getAuthErrorKind(error);
  const authErrorMessage =
    getAuthErrorMessage(error) ?? 'Authentication bootstrap failed.';

  if (authErrorKind === 'reauth-required') {
    return new AuthBootstrapError(authErrorMessage, {
      cause: error,
      kind: authErrorKind,
      messageKey: 'shared.auth.bootstrap.error.sessionInvalid',
      phase,
    });
  }

  if (authErrorKind === 'access-denied') {
    return new AuthBootstrapError(authErrorMessage, {
      cause: error,
      kind: authErrorKind,
      messageKey: 'shared.auth.bootstrap.error.accessDenied',
      phase,
    });
  }

  if (error instanceof Error) {
    return new AuthBootstrapError(error.message, {
      canRetry: true,
      cause: error,
      kind: 'retryable',
      messageKey: 'shared.auth.bootstrap.error.temporaryUnavailable',
      phase,
    });
  }

  return new AuthBootstrapError('Failed to finish authentication bootstrap.', {
    canRetry: true,
    cause: error,
    kind: 'retryable',
    messageKey: 'shared.auth.bootstrap.error.temporaryUnavailable',
    phase,
  });
};

/**
 * Fetches and caches the authenticated local user.
 *
 * @param queryClient - React Query client used to seed the current-user cache.
 * @returns Current local user returned by the backend.
 */
export const bootstrapCurrentUser = async (
  queryClient: QueryClient
): Promise<User> => {
  const currentUser = await V2AdminUsersService.getCurrentUser();
  queryClient.setQueryData(adminUsersQueryKeys.current(), currentUser);
  return currentUser;
};

/**
 * Generic retry helper for auth bootstrap operations.
 *
 * @param options - Retry configuration and operation.
 * @returns Successful operation result.
 */
export const retryOperation = async <T>({
  attempts,
  onRetry,
  operation,
  phase,
  shouldRetry,
}: {
  attempts: number;
  onRetry?: (error: AuthBootstrapError) => Promise<void>;
  operation: () => Promise<T>;
  phase: AuthBootstrapPhase;
  shouldRetry: (error: AuthBootstrapError) => boolean;
}): Promise<T> => {
  let lastError: AuthBootstrapError | null = null;

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      const normalizedError = normalizeBootstrapError(error, phase);
      lastError = normalizedError;

      if (attempt === attempts || !shouldRetry(normalizedError)) {
        throw normalizedError;
      }

      await onRetry?.(normalizedError);
      await sleep(getRetryDelay(attempt));
    }
  }

  throw (
    lastError ??
    new AuthBootstrapError('Auth bootstrap exhausted all retry attempts.', {
      canRetry: true,
      kind: 'retryable',
      messageKey: 'shared.auth.bootstrap.error.temporaryUnavailable',
      phase,
    })
  );
};

/**
 * Fetches a token with retry and timeout guards.
 *
 * @param getAccessTokenSilently - Auth0 token resolver.
 * @param phase - Bootstrap phase associated with the token fetch.
 * @param options - Optional Auth0 cache mode overrides.
 * @returns Access token string.
 */
export const fetchTokenWithRetries = async (
  getAccessTokenSilently: GetAccessTokenSilently,
  phase: AuthBootstrapPhase,
  options?: { cacheMode?: 'off' | 'on' | 'cache-only' }
) => {
  return await retryOperation({
    attempts: AUTH_RETRY_ATTEMPTS,
    phase,
    operation: async () =>
      await withTimeout(
        async () => await getAccessTokenSilently(options),
        TOKEN_REQUEST_TIMEOUT_IN_MS,
        'Timed out while waiting for Auth0 to issue a token.',
        phase
      ),
    shouldRetry: (error) => error.canRetry,
  });
};

/**
 * Bootstraps the current local user with retries.
 *
 * @param queryClient - React Query client used to seed the current-user cache.
 * @param getAccessTokenSilently - Auth0 token resolver used for one forced
 * token refresh after an authentication failure.
 * @returns Current local user returned by the backend.
 */
export const bootstrapCurrentUserWithRetries = async (
  queryClient: QueryClient,
  getAccessTokenSilently: GetAccessTokenSilently
) => {
  let forcedTokenRefreshAttempted = false;

  return await retryOperation({
    attempts: CURRENT_USER_RETRY_ATTEMPTS,
    phase: 'current-user',
    operation: async () =>
      await withTimeout(
        async () => await bootstrapCurrentUser(queryClient),
        CURRENT_USER_TIMEOUT_IN_MS,
        'Timed out while waiting for the current user bootstrap request.',
        'current-user'
      ),
    onRetry: async (error) => {
      if (error.kind !== 'reauth-required' || forcedTokenRefreshAttempted) {
        return;
      }

      forcedTokenRefreshAttempted = true;
      await fetchTokenWithRetries(getAccessTokenSilently, 'current-user', {
        cacheMode: 'off',
      });
    },
    shouldRetry: (error) =>
      error.canRetry ||
      (error.kind === 'reauth-required' && !forcedTokenRefreshAttempted),
  });
};

/**
 * Re-fetches permissions after the first-login bootstrap request.
 *
 * @param getAccessTokenSilently - Auth0 token resolver.
 * @returns Resolved permission list once Auth0 roles propagate.
 */
export const resolvePermissionsAfterBootstrap = async (
  getAccessTokenSilently: GetAccessTokenSilently
) => {
  for (let attempt = 1; attempt <= PERMISSIONS_REFRESH_ATTEMPTS; attempt += 1) {
    const refreshedToken = await fetchTokenWithRetries(
      getAccessTokenSilently,
      'permissions-refresh',
      {
        cacheMode: 'off',
      }
    );
    const refreshedPermissions = decodePermissions(refreshedToken);

    if (refreshedPermissions.length > 0) {
      return refreshedPermissions;
    }

    if (attempt < PERMISSIONS_REFRESH_ATTEMPTS) {
      await sleep(getRetryDelay(attempt));
    }
  }

  throw new AuthBootstrapError(
    'Permissions remained empty after the first-login refresh window.',
    {
      canRetry: true,
      kind: 'permissions-unavailable',
      messageKey: 'shared.auth.bootstrap.error.permissionsUnavailable',
      phase: 'permissions-refresh',
    }
  );
};

/**
 * Resolves the independent redirect budget for a recoverable bootstrap error.
 *
 * @param error - Normalized bootstrap error.
 * @returns Recovery reason, or undefined when no redirect should be attempted.
 */
export const getInteractiveRecoveryReason = (
  error: AuthBootstrapError
): AuthBootstrapRecoveryReason | undefined => {
  if (error.kind === 'identity-linked') {
    return 'identity-linked';
  }

  if (
    (error.phase === 'token' ||
      (error.phase === 'current-user' && error.kind === 'reauth-required')) &&
    (error.kind === 'reauth-required' || error.kind === 'retryable')
  ) {
    return 'token';
  }

  return undefined;
};

/**
 * Decides whether bootstrap should trigger one interactive Auth0 recovery.
 *
 * @param error - Normalized bootstrap error.
 * @returns True when the matching recovery redirect remains available.
 */
export const shouldAttemptInteractiveRecovery = (
  error: AuthBootstrapError
): boolean => {
  const recoveryReason = getInteractiveRecoveryReason(error);

  return (
    recoveryReason !== undefined &&
    !hasAuthBootstrapRecoveryAttempted(recoveryReason)
  );
};

/**
 * Decides whether a permissioned session can continue after a transient
 * current-user bootstrap failure.
 *
 * @param error - Normalized current-user bootstrap error.
 * @param permissions - Permissions already resolved from the access token.
 * @returns True when the app can continue using the current permission set.
 */
export const canContinueWithExistingPermissions = (
  error: AuthBootstrapError,
  permissions: string[]
): boolean => {
  return (
    permissions.length > 0 &&
    error.phase === 'current-user' &&
    error.kind === 'retryable'
  );
};
